// components/common/Navbar.jsx - COMPLETE REDESIGN
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { STORAGE_KEYS } from '../../utils/constants';
import { 
  UserIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ClipboardDocumentListIcon,
  WrenchScrewdriverIcon,
  ChevronDownIcon,
  HomeIcon,
  InformationCircleIcon,
  QuestionMarkCircleIcon,
  EnvelopeIcon,
  ShoppingBagIcon
} from '@heroicons/react/24/outline';

const Navbar = () => {
  const { user: contextUser, isAuthenticated, logout, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  // Enhanced navigation with icons
  const navigation = [
    { name: 'Home', href: '/', icon: HomeIcon },
    { name: 'About', href: '/about-us', icon: InformationCircleIcon },
    { name: 'FAQ', href: '/faq', icon: QuestionMarkCircleIcon },
    { name: 'Contact', href: '/contact-us', icon: EnvelopeIcon },
  ];

  // Service menu items for dropdown
  const serviceItems = [
    { name: 'Service History', href: '/service-history', icon: ClipboardDocumentListIcon, color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
    // { name: 'Spare Orders', href: '/spare-orders', icon: WrenchScrewdriverIcon, color: 'text-amber-600', bgColor: 'bg-amber-50' },
  ];

  // Load user from storage
  useEffect(() => {
    const loadUserFromStorage = () => {
      try {
        const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          setCurrentUser(parsedUser);
        }
      } catch (error) {
        console.error('Error loading user from localStorage:', error);
      }
    };
    loadUserFromStorage();
  }, [contextUser, isAuthenticated]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
      setIsMenuOpen(false);
      setIsUserMenuOpen(false);
      setCurrentUser(null);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      setCurrentUser(null);
      navigate('/');
    }
  };

  const getUserDisplayName = () => {
    const user = currentUser || contextUser;
    if (!user) return 'Guest';
    
    const userData = user.data || user;
    
    if (userData.name && userData.name.trim() && userData.name !== 'User') {
      return userData.name.trim();
    }
    if (userData.phoneNumber) {
      return userData.phoneNumber;
    }
    if (userData.email) {
      return userData.email.split('@')[0];
    }
    return 'User';
  };

  const getUserInitials = () => {
    const displayName = getUserDisplayName();
    
    if (displayName === 'Guest' || displayName === 'User') return 'U';
    
    const words = displayName.split(' ').filter(w => w.length > 0);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    
    return displayName.substring(0, 2).toUpperCase();
  };

  const getUserProfileImage = () => {
    const user = currentUser || contextUser;
    const userData = user?.data || user;
    return userData?.profile || null;
  };

  const user = currentUser || contextUser;
  const profileImage = getUserProfileImage();
  const displayName = getUserDisplayName();

  // Check if current path is active
  const isActive = (path) => location.pathname === path;
  const isServiceActive = () => serviceItems.some(item => item.href === location.pathname);

  return (
    <nav 
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-xl border-b border-gray-100' 
          : 'bg-white border-b border-gray-200'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-18 md:h-20">
          
          {/* Logo - Modern with hover effect */}
          <Link to="/" className="flex items-center group relative z-10">
            <div className="relative overflow-hidden rounded-lg">
              <img 
                src="/vite.svg" 
                alt="okmechanic" 
                className="h-9 w-auto transition-all duration-300 group-hover:scale-110 group-hover:rotate-1"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/0 via-blue-600/10 to-blue-600/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
            </div>
            
          </Link>

          {/* Desktop Navigation - Pill style with icons */}
          <div className="hidden lg:flex items-center space-x-1 bg-gray-100/80 p-1.5 rounded-2xl">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
                    isActive(item.href)
                      ? 'bg-white text-blue-600 shadow-md scale-105'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-white/60'
                  }`}
                >
                  <Icon className={`h-4 w-4 ${isActive(item.href) ? 'text-blue-600' : 'text-gray-400'}`} />
                  {item.name}
                </Link>
              );
            })}
          </div>

          {/* Desktop Right Side - New Layout */}
          <div className="hidden md:flex items-center gap-3">
            {loading ? (
              <div className="flex items-center gap-2 px-4 py-2">
                <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                <span className="text-sm text-gray-500 font-medium">Loading...</span>
              </div>
            ) : isAuthenticated && user ? (
              <div className="flex items-center gap-3">
                
                {/* Service Quick Links - Compact pills */}
                <div className="flex items-center gap-2 pr-3 border-r border-gray-200">
                  {serviceItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`group relative p-2.5 rounded-xl transition-all duration-200 ${
                          isActive(item.href)
                            ? `${item.bgColor} ${item.color} shadow-md`
                            : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                        }`}
                        title={item.name}
                      >
                        <Icon className="h-5 w-5" />
                        {isActive(item.href) && (
                          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-current" />
                        )}
                      </Link>
                    );
                  })}
                </div>

                {/* User Dropdown - Modern glassmorphism */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className={`flex items-center gap-3 pl-2 pr-4 py-2 rounded-2xl transition-all duration-200 ${
                      isUserMenuOpen 
                        ? 'bg-blue-50 ring-2 ring-blue-100' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    {/* Avatar */}
                    <div className="relative">
                      {profileImage ? (
                        <img 
                          src={profileImage} 
                          alt={displayName}
                          className="h-9 w-9 rounded-full object-cover ring-2 ring-white shadow-md"
                        />
                      ) : (
                        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shadow-md">
                          {getUserInitials()}
                        </div>
                      )}
                      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
                    </div>
                    
                    {/* Name & Chevron */}
                    <div className="hidden xl:block text-left">
                      <p className="text-sm font-bold text-gray-800 leading-tight">{displayName}</p>
                      <p className="text-xs text-gray-500">Account</p>
                    </div>
                    <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu - Modern card style */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-3 w-72 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                      {/* User Header */}
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                          {profileImage ? (
                            <img src={profileImage} alt={displayName} className="h-12 w-12 rounded-full object-cover ring-2 ring-white shadow-sm" />
                          ) : (
                            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-sm">
                              {getUserInitials()}
                            </div>
                          )}
                          <div>
                            <p className="font-bold text-gray-900">{displayName}</p>
                            <p className="text-xs text-gray-500">Logged in</p>
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="p-2">
                        <Link
                          to="/profile"
                          className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <UserIcon className="h-4 w-4 text-blue-600" />
                          </div>
                          My Profile
                        </Link>

                        <div className="my-2 border-t border-gray-100" />

                        {serviceItems.map((item) => {
                          const Icon = item.icon;
                          return (
                            <Link
                              key={item.name}
                              to={item.href}
                              className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <div className={`p-2 rounded-lg ${item.bgColor}`}>
                                <Icon className={`h-4 w-4 ${item.color}`} />
                              </div>
                              {item.name}
                            </Link>
                          );
                        })}

                        <div className="my-2 border-t border-gray-100" />

                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-colors w-full"
                        >
                          <div className="p-2 bg-red-100 rounded-lg">
                            <ArrowRightOnRectangleIcon className="h-4 w-4 text-red-600" />
                          </div>
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="px-5 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-xl shadow-lg hover:shadow-blue-500/25 transition-all duration-200 transform hover:-translate-y-0.5"
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button - Animated hamburger */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="relative p-2 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none"
              aria-label="Toggle navigation menu"
            >
              <div className="w-6 h-6 flex flex-col justify-center items-center">
                <span className={`block w-5 h-0.5 bg-current rounded-full transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-1' : '-translate-y-1'}`} />
                <span className={`block w-5 h-0.5 bg-current rounded-full transition-all duration-300 ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`} />
                <span className={`block w-5 h-0.5 bg-current rounded-full transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-1' : 'translate-y-1'}`} />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation - Full screen overlay style */}
      {isMenuOpen && (
        <div className="md:hidden fixed inset-0 top-[72px] bg-white z-40 overflow-y-auto">
          <div className="px-4 py-6 space-y-6">
            
            {/* Main Navigation */}
            <div className="space-y-2">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 px-2">Menu</p>
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center gap-4 px-4 py-4 rounded-2xl text-base font-bold transition-all ${
                      isActive(item.href)
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className={`p-2 rounded-xl ${isActive(item.href) ? 'bg-white/20' : 'bg-gray-200'}`}>
                      <Icon className={`h-5 w-5 ${isActive(item.href) ? 'text-white' : 'text-gray-600'}`} />
                    </div>
                    {item.name}
                  </Link>
                );
              })}
            </div>

            {/* Service Section for Mobile */}
            {isAuthenticated && user && (
              <div className="space-y-2">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 px-2">My Services</p>
                <div className="grid grid-cols-2 gap-3">
                  {serviceItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all ${
                          isActive(item.href)
                            ? `border-current ${item.color} ${item.bgColor}`
                            : 'border-gray-100 hover:border-gray-200 text-gray-600'
                        }`}
                      >
                        <Icon className={`h-6 w-6 ${isActive(item.href) ? item.color : 'text-gray-500'}`} />
                        <span className="text-sm font-bold text-center">{item.name}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* User Section */}
            <div className="pt-4 border-t border-gray-200">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                </div>
              ) : isAuthenticated && user ? (
                <div className="space-y-4">
                  {/* User Card */}
                  <Link
                    to="/profile"
                    className="flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100"
                  >
                    {profileImage ? (
                      <img src={profileImage} alt={displayName} className="h-14 w-14 rounded-full object-cover ring-2 ring-white shadow-md" />
                    ) : (
                      <div className="h-14 w-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                        {getUserInitials()}
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 text-lg">{displayName}</p>
                      <p className="text-sm text-blue-600 font-medium">View Profile →</p>
                    </div>
                  </Link>

                  {/* Logout Button */}
                  <button
                    onClick={handleLogout}
                    className="flex items-center justify-center gap-2 w-full px-4 py-4 rounded-2xl text-base font-bold text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5" />
                    Sign Out
                  </button>
                </div>
              ) : (
                <div className="space-y-3 px-2">
                  <Link
                    to="/login"
                    className="block w-full px-4 py-4 rounded-2xl text-base font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors text-center"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="block w-full px-4 py-4 rounded-2xl text-base font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all text-center shadow-lg"
                  >
                    Create Account
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;